import {
  PM_CHAT_HISTORY_LIMIT,
  PRE_BUILD_TASK_STATUSES,
  TASK_CHAT_HISTORY_LIMIT,
  parseMentions,
  type RunnerMode,
  type AgentMode,
  type TaskContext,
} from "@project/shared";
import { buildChatInstructions } from "./chat-instructions.js";
import {
  buildReviewHostHoldParts,
  findLastAgentMessageIndex,
  isCodeReviewRun,
  isReviewHostHold,
  messagesAfterCursor,
  relaunchMessageBatch,
} from "./relaunch-hold.js";
import { buildPackRunnerInstructions } from "./pack-runner-prompt.js";
import {
  baseDiffCommand,
  formatChatHistory,
  formatRepoRefs,
  formatReferenceProjects,
  formatProjectObjectives,
  formatRecentRelatedTasks,
  formatIncidents,
  formatTaskFile,
} from "./prompt-formatters.js";
import { resolveTagContext } from "./tag-context-resolver.js";
import { truncatePlanForPrompt } from "./prompt-truncation.js";
import { buildPmRelaunchParts } from "./pm-relaunch-instructions.js";

export { buildSystemPrompt } from "./system-prompt.js";

function detectRelaunchScenario(
  context: TaskContext,
  trustChatHistory = false,
): "fresh" | "idle_relaunch" | "feedback_relaunch" {
  // Cursor is the primary signal: if set, the agent has completed at least
  // one turn on this session, so this is a relaunch.
  if (context.lastSeenMessageId) {
    const newMessages = messagesAfterCursor(context.chatHistory, context.lastSeenMessageId);
    const hasNewUserMessages = newMessages.some((m) => m.role === "user");
    return hasNewUserMessages ? "feedback_relaunch" : "idle_relaunch";
  }

  // No cursor — fall back to chat-history heuristics for backward compatibility
  // with sessions that predate the cursor and for PM/Pack Runner agents whose
  // own prior chat messages are themselves a reliable "prior work" indicator.
  const lastAgentIdx = findLastAgentMessageIndex(context.chatHistory);
  if (lastAgentIdx === -1) return "fresh";

  // githubPRUrl signals prior work survived across restarts. claudeSessionId
  // is intentionally NOT consulted here: it persists across crashed sessions
  // and would cause a false "prior work done" reading on a fresh relaunch.
  const hasPriorWork = !!context.githubPRUrl || trustChatHistory;
  if (!hasPriorWork) return "fresh";

  const messagesAfterAgent = context.chatHistory.slice(lastAgentIdx + 1);
  const hasNewUserMessages = messagesAfterAgent.some((m) => m.role === "user");
  return hasNewUserMessages ? "feedback_relaunch" : "idle_relaunch";
}

function buildRelaunchWithSession(
  mode: RunnerMode | undefined,
  context: TaskContext,
  agentMode?: AgentMode | null,
  isAuto?: boolean,
): string | null {
  const scenario = detectRelaunchScenario(context);
  // Cursor is the authoritative relaunch signal; claudeSessionId is kept as a
  // fallback for sessions predating the cursor. Either proves the agent has
  // completed at least one turn on this session, so a "relaunch" prompt —
  // rather than the fresh-start prompt — is appropriate.
  const hasPriorTurn = !!context.lastSeenMessageId || !!context.claudeSessionId;
  if (!hasPriorTurn || scenario === "fresh") return null;

  const parts: string[] = [];
  const lastAgentIdx = findLastAgentMessageIndex(context.chatHistory);
  const allNew = relaunchMessageBatch(context);

  // Chat cards get the same bare conversational instructions on every turn —
  // never the branch/commit/PR relaunch boilerplate below.
  if (agentMode === "chat") {
    return buildChatInstructions(context, scenario, allNew).join("\n");
  }

  if (mode !== "pm" && isReviewHostHold(context, allNew, isCodeReviewRun(mode, agentMode))) {
    return buildReviewHostHoldParts(
      context,
      allNew.filter((m) => m.role === "user"),
    ).join("\n");
  }

  if (mode === "pm") {
    parts.push(...buildPmRelaunchParts(context, lastAgentIdx, isAuto, agentMode));
  } else if (scenario === "feedback_relaunch") {
    const newMessages = allNew.filter((m) => m.role === "user");
    parts.push(
      `You have been relaunched with new feedback.`,
      `Work on the git branch "${context.githubBranch}". Stay on this branch — do not checkout or create other branches.`,
      `\nNew messages since your last run:`,
      ...newMessages.map((m) => `[${m.userName ?? "user"}]: ${m.content}`),
      `\nAddress the requested changes. Do NOT re-investigate the codebase from scratch or write a new plan — review the feedback and implement the changes directly.`,
      `Commit and push your updates.`,
    );
    if (context.githubPRUrl) {
      parts.push(
        `An existing PR is open at ${context.githubPRUrl} — push to the same branch. Do NOT create a new PR.`,
      );
    } else {
      parts.push(
        `When finished, use the mcp__conveyor__create_pull_request tool to open a PR. Do NOT use gh CLI.`,
      );
    }
  } else {
    parts.push(
      `You were relaunched but no new instructions have been given since your last run.`,
      `Work on the git branch "${context.githubBranch}". Stay on this branch — do not checkout or create other branches.`,
      `Run \`git log --oneline -10\` to review what you already committed.`,
      `Review the current state of the codebase and verify everything is working correctly.`,
    );
    if (agentMode === "auto" || agentMode === "building" || isAuto) {
      parts.push(
        `If work is incomplete, continue implementing the plan. When finished, commit, push, and use mcp__conveyor__create_pull_request to open a PR.`,
        `Do NOT go idle or wait for instructions — you are in auto mode.`,
      );
    } else {
      parts.push(
        `Post a brief status update with post_to_chat (your turn output is NOT shown in chat — post_to_chat is how the team sees it), then wait for further instructions.`,
      );
    }
    if (context.githubPRUrl) {
      parts.push(`An existing PR is open at ${context.githubPRUrl}. Do not create a new PR.`);
    }
  }

  return parts.join("\n");
}

/** Tag ids deep-linked (`@[tag:id]`) in the card's description/plan. */
function mentionedTagIdsFrom(context: TaskContext): string[] {
  const body = `${context.description ?? ""}\n${context.plan ?? ""}`;
  return [
    ...new Set(
      parseMentions(body)
        .filter((token) => token.type === "tag")
        .map((token) => token.id),
    ),
  ];
}

async function resolveTaskTagContext(
  context: TaskContext,
  runnerMode?: RunnerMode,
): Promise<string | null> {
  const mentionedTagIds = mentionedTagIdsFrom(context);
  const hasTags = !!context.projectTags?.length && !!context.taskTagIds?.length;
  const hasSubProjectPaths = !!context.subProject?.contextPaths?.length;
  if (!hasTags && !hasSubProjectPaths && mentionedTagIds.length === 0) return null;
  const { injectedSection } = await resolveTagContext(
    context.projectTags,
    context.taskTagIds ?? [],
    context.model,
    context.agentSettings?.betas,
    runnerMode,
    context.subProject ?? null,
    mentionedTagIds,
  );
  return injectedSection || null;
}

// oxlint-disable-next-line complexity -- simple field mapping, not real branching complexity
async function buildTaskBody(context: TaskContext, runnerMode?: RunnerMode): Promise<string[]> {
  const parts: string[] = [];
  parts.push(`# Task: ${context.title}`);
  if (context.projectName) {
    const descLine = context.projectDescription ? `\n${context.projectDescription}` : "";
    parts.push(`\n## Project: ${context.projectName}${descLine}`);
  }
  if (context.subProject?.rootPath) {
    parts.push(
      `\n## Sub-project scope: ${context.subProject.name}`,
      `This task belongs to the "${context.subProject.name}" sub-project, which owns \`${context.subProject.rootPath}\`.`,
      `- Read and reference the whole repository freely.`,
      `- Substantive code changes belong under \`${context.subProject.rootPath}\`. Trivial wiring elsewhere (exports, route registration) is fine when required.`,
      `- If the work genuinely requires substantive changes outside that folder, do NOT make them here — create a task on the parent project board (as part of a pack with this one) describing the needed change.`,
    );
  }
  if (context.description) {
    parts.push(`\n## Description\n${context.description}`);
  }
  if (context.plan) {
    parts.push(`\n## Plan\n${truncatePlanForPrompt(context.plan)}`);
  }

  if (context.files && context.files.length > 0) {
    parts.push(`\n## Attached Files`);
    for (const file of context.files) {
      parts.push(...formatTaskFile(file));
    }
  }

  if (context.repoRefs && context.repoRefs.length > 0) {
    parts.push(...formatRepoRefs(context.repoRefs));
  }

  if (context.referenceProjects && context.referenceProjects.length > 0) {
    parts.push(...formatReferenceProjects(context.referenceProjects));
  }

  const tagSection = await resolveTaskTagContext(context, runnerMode);
  if (tagSection) parts.push(tagSection);

  // Project objectives and related tasks help PM planning but are redundant for task agents
  // (task agents already have a plan that incorporates this context)
  if (runnerMode !== "task") {
    if (context.projectObjectives && context.projectObjectives.length > 0) {
      parts.push(...formatProjectObjectives(context.projectObjectives));
    }
    if (context.recentRelatedTasks && context.recentRelatedTasks.length > 0) {
      parts.push(...formatRecentRelatedTasks(context.recentRelatedTasks));
    }
  }

  if (context.incidents && context.incidents.length > 0) {
    parts.push(...formatIncidents(context.incidents));
  }

  if (context.chatHistory.length > 0) {
    const chatLimit = runnerMode === "task" ? TASK_CHAT_HISTORY_LIMIT : PM_CHAT_HISTORY_LIMIT;
    parts.push(...formatChatHistory(context.chatHistory, chatLimit));
  }

  return parts;
}

// Code-review runs never reach here — `buildInstructions` routes them to
// `buildFreshCodeReviewInstructions` ahead of the scenario branching.
function buildFreshInstructions(
  isPm: boolean,
  isAutoMode: boolean,
  context: TaskContext,
  agentMode?: AgentMode | null,
): string[] {
  // After auto→building transition, agent needs building instructions
  // PM→building only happens through auto mode, so always include anti-idle guidance
  if (isPm && agentMode === "building") {
    return [
      `Your plan has been approved. Begin implementing it now.`,
      `Work on the git branch "${context.githubBranch}". Stay on this branch — do not checkout or create other branches.`,
      `Start by reading the relevant source files mentioned in the plan, then write code.`,
      `When finished, use the mcp__conveyor__create_pull_request tool to open a PR. Do NOT use gh CLI.`,
      `\nCRITICAL: You are in Auto mode. Do NOT report status, ask for confirmation, or go idle without making code changes.`,
      `Your FIRST action must be reading source files from the plan, then immediately writing code.`,
      `Do NOT summarize the plan or say "ready to implement" — start implementing.`,
      `When all changes are ready, use mcp__conveyor__create_pull_request to open a PR.`,
      `If you are genuinely blocked, explain the specific blocker — do not go idle silently.`,
    ];
  }
  if (isAutoMode && isPm) {
    if (context.plan?.trim()) {
      return [
        `You are operating autonomously. A plan already exists for this task.`,
        `Begin implementing it now — do NOT re-plan or wait for team input.`,
        `Refine story points, title, and tags via update_task_properties if they look like placeholders.`,
      ];
    }
    return [
      `You are operating autonomously. No plan is saved on this card yet.`,
      `1. Search the codebase (grep/glob) to locate relevant files, then read the critical ones`,
      `2. Save a concise plan with update_task_plan BEFORE writing any code — the plan is a record for the team, not a gate; never pause or wait for approval`,
      `3. Implement the work, keeping the plan current if your approach changes materially`,
      `4. Refine story points, tags, and title (update_task_properties) if they look like placeholders`,
      `Do NOT wait for team input — proceed autonomously.`,
    ];
  }
  if (isPm && context.isParentTask) {
    return [
      `You are the project manager for this task and its subtasks.`,
      `Review existing subtasks via \`list_subtasks\` and the chat history before taking action.`,
      `Read the task description and chat history carefully — the team has provided the initial context below. Acknowledge what they've asked for and respond in chat before taking silent tool actions.`,
      `Start planning now — explore the codebase, ask clarifying questions if needed, and propose a subtask breakdown. Do not wait for additional team input before engaging.`,
      `When you finish planning, save the plan with update_task_plan, post a short summary for the team with post_to_chat (your turn output is NOT shown in chat), then end your turn.`,
    ];
  }
  if (isPm) {
    return [
      `You are the project manager for this task — a thoughtful, collaborative planner, not the implementer.`,
      `Your job right now is to help the team turn this request into a clear, well-scoped plan. Be a helpful planning partner: think out loud, surface trade-offs, and keep the human in the loop.`,
      `Read the task description and chat history carefully — the team has provided the initial context below. Acknowledge what they've asked for and respond in chat before taking silent tool actions.`,
      `Start planning now — explore the codebase, ask clarifying questions if anything is ambiguous, and draft a plan. Do not wait for additional team input before engaging; the initial message IS the team engaging.`,
      `When you finish planning, save the plan with update_task_plan, post a summary of the plan for the team with post_to_chat (your turn output is NOT shown in chat), then end your turn. A separate task agent will execute the plan after review.`,
    ];
  }
  return buildFreshLeafInstructions(context, isAutoMode);
}

function buildFreshLeafInstructions(context: TaskContext, isAutoMode: boolean): string[] {
  const parts = context.plan?.trim()
    ? [`Start now by reading the source files the plan names, then writing code.`]
    : [
        `No plan is saved on this card yet. Investigate the task briefly (search first, read only critical files), then save a concise implementation plan with update_task_plan (file:line citations) BEFORE you start writing code.`,
        `The plan is a step, not the goal: the moment it's posted, IMPLEMENT it in code — do NOT pause, wait for approval, or treat the posted plan as the deliverable. The card is already In Progress and advances automatically.`,
      ];
  // Gate guidance lives in ONE place — the "Pre-PR Protocol" section of the
  // building-mode system prompt (`mode-prompt.ts`). This is a pointer, not a
  // second copy: the same ~15 lines injected twice per session read as two
  // separate instructions and invited a second gate pass.
  const base = context.baseBranch?.trim() || "dev";
  parts.push(
    `Post to chat when you begin implementing and again when the PR is ready.`,
    `Follow the **Pre-PR Protocol** in your system prompt: commit, then sync the base (\`git fetch origin ${base} && git merge origin/${base} --no-edit\`), then ONE verification pass — \`bun run check\` (lint + typecheck) plus \`bun run test:affected\` (tests scoped to your diff; docs-only changes need no local gates) — then open the PR. Do NOT re-merge the base or re-run a gate that already passed.`,
    `If a gate fails, iterate on just the failing test files (\`bun run --cwd <pkg> test:unit <files>\`) and confirm with a single final scoped-gate run. Do NOT open a PR with known failing gates.`,
    `Open the PR with mcp__conveyor__create_pull_request when the work is done and the gates are green.`,
  );
  if (isAutoMode) {
    parts.push(
      `\nCRITICAL: You are in Auto mode. Your job is to BUILD the change, not to produce a plan — making a plan and opening a PR of the plan is NOT the goal. The plan is only an intermediate step; you must then write the code that implements it.`,
      `Do NOT report status, ask for confirmation, or go idle without making code changes. Do NOT summarize the plan or say "ready to implement" — start implementing immediately.`,
      `Your pull request MUST contain the actual code implementation. Never open a plan-only or empty-diff PR. (If the task genuinely needs no code changes, do not open a PR — deliver the result in chat and mark the card Complete.)`,
      `When the implementation is complete and verified, you MUST use mcp__conveyor__create_pull_request to open a PR before finishing.`,
      `If you are genuinely blocked, explain the specific blocker — do not go idle silently.`,
    );
  }
  return parts;
}

function buildFreshCodeReviewInstructions(context: TaskContext): string[] {
  const parts = [
    `Perform the automated code review for this PR.`,
    `Work on the git branch "${context.githubBranch}". Stay on this branch for the entire review. Do not checkout or create other branches.`,
    `Start by establishing what you are actually reviewing${context.githubPRUrl ? `: \`gh pr view ${context.githubPRUrl} --json state,baseRefName,headRefOid,files\`` : ` — the PR's own base ref, head SHA and changed-file list`}. Do this BEFORE any git diff: the PR's base is often not the project default branch, and asking the PR costs one call instead of six.`,
    `Then inspect the submitted changes with \`${baseDiffCommand(context.baseBranch)}\`. If that disagrees with the PR's file list, trust the PR: diff the head SHA directly (\`git diff <headSha>^ <headSha>\`) and review that.`,
    `Your checkout can be stale, and so can the remote branch tip — a merged PR's commit may be reachable only by SHA. If the PR reports state MERGED or CLOSED, say so and review the recorded head SHA rather than assuming your working tree matches it.`,
    `Read the code under review out of the PR's commit, NOT the working tree: \`git show <sha>:<path>\` and \`git grep <pattern> <sha>\`. Grepping the working tree when the PR is based elsewhere returns confident, wrong answers — it has produced false review findings.`,
    `Review for correctness, security, performance, error handling, test coverage, and consistency with existing patterns.`,
    `Consult the card's tag Reference Guides in the brief above for the domain rules that apply to this diff — they are the conventions the change must follow, and a contradiction of them is a review finding.`,
    `If small fixes are needed, make them directly, commit, push, and re-review the result.`,
    `Use approve_code_review when the PR passes review.`,
    `Use request_code_changes when substantive issues remain that you cannot fix directly.`,
  ];
  if (context.githubPRUrl) {
    parts.push(`The PR under review is ${context.githubPRUrl}. Do not create a new PR.`);
  }
  return parts;
}

function buildFeedbackInstructions(
  context: TaskContext,
  isPm: boolean,
  agentMode?: AgentMode | null,
  isAuto?: boolean,
): string[] {
  const lastAgentIdx = findLastAgentMessageIndex(context.chatHistory);
  const newMessages = context.chatHistory.slice(lastAgentIdx + 1).filter((m) => m.role === "user");
  if (isPm) {
    const parts = [
      `You were relaunched with new feedback since your last run.`,
      `You are the project manager for this task.`,
      `\nNew messages since your last run:`,
      ...newMessages.map((m) => `[${m.userName ?? "user"}]: ${m.content}`),
    ];
    if (isAuto && (agentMode === "building" || agentMode === "review")) {
      parts.push(
        `\nYour plan has been approved. Address the feedback above, then begin implementing.`,
        `Work on the git branch "${context.githubBranch}". Stay on this branch — do not checkout or create other branches.`,
        `Start by reading the relevant source files mentioned in the plan, then write code.`,
        `When finished, use the mcp__conveyor__create_pull_request tool to open a PR. Do NOT use gh CLI.`,
      );
    } else if (isAuto) {
      parts.push(
        `\nYou are in auto mode. Address the feedback above and continue building — update the saved plan with update_task_plan if the feedback changes your approach.`,
        `Do NOT wait for additional team input — the messages above ARE the team's input. Proceed autonomously.`,
      );
    } else {
      parts.push(
        `\nReview these messages and wait for the team to provide instructions before taking action.`,
      );
    }
    return parts;
  }
  const parts = [
    `You have been relaunched to address feedback on your previous work.`,
    `Work on the git branch "${context.githubBranch}". Stay on this branch — do not checkout or create other branches.`,
    `Start by running \`${baseDiffCommand(context.baseBranch, "--stat")}\` to review what you already committed on this branch.`,
    `\nNew messages since your last run:`,
    ...newMessages.map((m) => `[${m.userName ?? "user"}]: ${m.content}`),
    `\nAddress the requested changes directly. Do NOT re-investigate the codebase from scratch or write a new plan — go straight to implementing the feedback.`,
    // Scoped to what the follow-up actually changed. An unconditional full
    // re-verify on every relaunch made a one-line or docs-only follow-up cost
    // the same gate time as the original build.
    `Re-verify in proportion to what THIS follow-up changed, not the whole branch: a docs/\`.claude\`-only change needs no local gates; a code change runs \`bun run check\` plus the directly-affected test files (\`bun run --cwd <pkg> test:unit <files>\`); escalate to a full \`bun run test:affected\` only when the follow-up touched \`packages/shared\`, \`packages/db\`, or root config, or when no gate has ever run on this branch. Fix any failures before pushing — relaunches are the most common place verification gets skipped.`,
    `Implement your updates and open a PR when finished.`,
  ];
  if (context.githubPRUrl) {
    parts.push(
      `An existing PR is open at ${context.githubPRUrl} — push to the same branch to update it. Do NOT create a new PR.`,
    );
  } else {
    parts.push(
      `When finished, use the mcp__conveyor__create_pull_request tool to open a PR. Do NOT use gh CLI or any other method to create PRs.`,
    );
  }
  return parts;
}

function buildIdleRelaunchInstructions(
  context: TaskContext,
  isPm: boolean,
  agentMode?: AgentMode | null,
  isAuto?: boolean,
): string[] {
  if (isPm && agentMode === "auto" && PRE_BUILD_TASK_STATUSES.has(context.status ?? "")) {
    if (context.plan?.trim()) {
      return [
        `You were relaunched in auto mode. A plan already exists for this task.`,
        `Begin implementing it now — refine story points, title, and tags via update_task_properties if they look like placeholders.`,
        `Do NOT wait for instructions or go idle — you are in auto mode.`,
      ];
    }
    return [
      `You were relaunched in auto mode. Continue building autonomously.`,
      `No plan is saved on this card yet — save a concise plan with update_task_plan before writing further code; never pause or wait for approval.`,
      `Do NOT wait for instructions or go idle — you are in auto mode.`,
    ];
  }

  if (isPm && !(agentMode === "building" || agentMode === "review" || agentMode === "auto")) {
    return [
      `You were relaunched but no new instructions have been given since your last run.`,
      `You are the project manager for this task.`,
      `Wait for the team to provide instructions before taking action.`,
    ];
  }

  // Task agent or PM in building/review/auto mode — check agentMode independently
  // of isAuto as defense-in-depth against stale isAuto values.
  const isAutoMode = agentMode === "auto" || agentMode === "building" || isAuto;
  const parts = [
    `You were relaunched but no new instructions have been given since your last run.`,
    `Work on the git branch "${context.githubBranch}". Stay on this branch — do not checkout or create other branches.`,
    `Run \`git log --oneline -10\` to review what you already committed, then verify the current state is correct.`,
  ];
  if (isAutoMode) {
    parts.push(
      `If work is incomplete, continue implementing the plan. When finished, use mcp__conveyor__create_pull_request to open a PR.`,
      `Do NOT go idle or wait for instructions — you are in auto mode.`,
    );
  } else {
    parts.push(
      `Post a brief status update summarizing where things stand with post_to_chat (your turn output is NOT shown in chat — post_to_chat is how the team sees it).`,
      `Then wait for further instructions — do NOT redo work that was already completed.`,
    );
  }
  if (context.githubPRUrl) {
    parts.push(`An existing PR is open at ${context.githubPRUrl}. Do not create a new PR.`);
  }
  return parts;
}

function buildInstructions(
  mode: RunnerMode | undefined,
  context: TaskContext,
  scenario: "fresh" | "idle_relaunch" | "feedback_relaunch",
  agentMode?: AgentMode | null,
  isAuto?: boolean,
): string[] {
  const parts: string[] = [`\n## Instructions`];

  const isPm = mode === "pm";
  const isCodeReview = mode === "code-review" || (!isPm && agentMode === "review");

  // Chat cards route ahead of every other shape: a chat card is a conversation
  // first, so it never gets leaf-builder, PM, or review instructions.
  if (agentMode === "chat") {
    parts.push(...buildChatInstructions(context, scenario, relaunchMessageBatch(context)));
    return parts;
  }

  // A review run's first turn ALWAYS gets reviewer instructions. The scenario
  // detector reads the card's builder chat plus `githubPRUrl` as "prior work",
  // so on a real card it routed the reviewer's opening turn to
  // idle/feedback-relaunch builder text ("continue implementing the plan…").
  // Resumed review sessions never reach here — they short-circuit in
  // `buildRelaunchWithSession` — and `isReviewHostHold` requires
  // `isCodeReview === false`, so the builder-hold path is untouched. New chat
  // messages still reach the reviewer through the body's chat-history section.
  if (isCodeReview) {
    parts.push(...buildFreshCodeReviewInstructions(context));
    return parts;
  }

  if (scenario === "fresh") {
    // The mode label is "building" once auto resolves at boot, so key the
    // auto-specific instructions off isAuto as well.
    parts.push(
      ...buildFreshInstructions(isPm, agentMode === "auto" || !!isAuto, context, agentMode),
    );
    return parts;
  }

  // A relaunch on a task whose PR is already under review, with nothing
  // actionable in the new batch, is a review-host wake — hold rather than take
  // an implement turn. Covers both relaunch shapes: a pure host wake arrives as
  // idle_relaunch, and one that coincides with an external worker's chat post
  // arrives as feedback_relaunch.
  const newBatch = relaunchMessageBatch(context);
  if (!isPm && isReviewHostHold(context, newBatch, isCodeReview)) {
    parts.push(
      ...buildReviewHostHoldParts(
        context,
        newBatch.filter((m) => m.role === "user"),
      ),
    );
    return parts;
  }

  if (scenario === "idle_relaunch") {
    parts.push(...buildIdleRelaunchInstructions(context, isPm, agentMode, isAuto));
    return parts;
  }

  parts.push(...buildFeedbackInstructions(context, isPm, agentMode, isAuto));
  return parts;
}

export async function buildInitialPrompt(
  mode: RunnerMode | undefined,
  context: TaskContext,
  isAuto?: boolean,
  agentMode?: AgentMode | null,
): Promise<string> {
  // RunnerMode "pack" is the first-class gate (see system-prompt.ts); the pm
  // clause is the legacy ProjectRunner gate — kept harmless.
  const isPackRunner = mode === "pack" || (mode === "pm" && !!isAuto && !!context.isParentTask);

  if (!isPackRunner) {
    const sessionRelaunch = buildRelaunchWithSession(mode, context, agentMode, isAuto);
    if (sessionRelaunch) return sessionRelaunch;
  }

  const isPm = mode === "pm";
  let scenario = detectRelaunchScenario(context, isPm);

  // After auto-mode transition from planning to building, planning-phase chat
  // history causes "idle_relaunch" even though no building work has started.
  // Override to "fresh" so the agent gets "begin implementing" instructions.
  if (
    isPm &&
    agentMode === "building" &&
    isAuto &&
    !context.claudeSessionId &&
    !context.githubPRUrl &&
    scenario === "idle_relaunch"
  ) {
    scenario = "fresh";
  }
  const body = await buildTaskBody(context, mode);
  const instructions = isPackRunner
    ? buildPackRunnerInstructions(context, scenario)
    : buildInstructions(mode, context, scenario, agentMode, isAuto);
  return [...body, ...instructions].join("\n");
}
